import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Clipboard, Settings, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { StandardsSelector } from "@/components/StandardsSelector";
import { ProjectForm } from "@/components/ProjectForm";
import { AppNavbar } from "@/components/layout/AppNavbar";

interface Project {
  id: string;
  title: string;
  description: string | null;
  status: string;
  progress: number;
  standard_id: string;
  standard_title?: string;
  standard_category?: string;
}

const Dashboard = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<any>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchProjects();
  }, []);
  
  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          standards:standard_id (
            title,
            category
          )
        `);
      
      if (error) {
        throw error;
      }
      
      if (data) {
        const transformedData = data.map(project => ({
          ...project,
          standard_title: project.standards?.title,
          standard_category: project.standards?.category
        }));
        
        setProjects(transformedData);
      }
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      toast.error(error.message || "Error loading projects");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast.success("You have been logged out");
      navigate("/login");
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message || "Logout failed");
    }
  };

  const addProject = () => {
    setShowSelector(true);
  };
  
  const handleSelectStandard = (standard: any) => {
    setSelectedStandard(standard);
    setShowSelector(false);
    setShowProjectForm(true);
  };
  
  const handleCancelSelection = () => {
    setShowSelector(false);
  };
  
  const handleAddProject = (projectData: any) => {
    fetchProjects();
    setShowProjectForm(false);
    setSelectedStandard(null);
  };
  
  const handleCancelProjectForm = () => {
    setShowProjectForm(false);
    setSelectedStandard(null);
  };
  
  const filteredProjects = projects.filter(project => 
    project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.standard_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.standard_category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppNavbar />

      <main className="flex-1 container py-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Your Projects</h1>
          <Button onClick={addProject}>
            <Plus className="h-4 w-4 mr-2" />
            Add Project
          </Button>
        </div>
        
        <div className="relative mb-6">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <div className="grid grid-cols-12 py-3 px-4 border-b border-border font-medium text-muted-foreground">
            <div className="col-span-4">Project</div>
            <div className="col-span-3">Standard</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1">Progress</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          
          {isLoading ? (
            <div className="py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-muted-foreground">Loading projects...</p>
            </div>
          ) : filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="grid grid-cols-12 py-4 px-4 border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
              >
                <div className="col-span-4 font-medium">{project.title}</div>
                <div className="col-span-3 text-muted-foreground">{project.standard_title}</div>
                <div className="col-span-2 text-muted-foreground">{project.standard_category}</div>
                <div className="col-span-1">
                  <span className="inline-block bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
                    {project.progress}%
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title="View details"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <Clipboard className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" title="Settings">
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No projects found. Try adjusting your search or add a new project.
            </div>
          )}
        </div>
      </main>

      {showSelector && (
        <StandardsSelector 
          onSelectStandard={handleSelectStandard} 
          onCancel={handleCancelSelection}
          modalTitle="Select a Standard for Your Project" 
        />
      )}

      {showProjectForm && selectedStandard && (
        <ProjectForm 
          selectedStandard={selectedStandard}
          onSubmit={handleAddProject}
          onCancel={handleCancelProjectForm}
        />
      )}
    </div>
  );
};

export default Dashboard;
